import { newSearch, handleFallback } from "../actions"
import { SearchQuery } from "../api/types"
import { FullConfig } from "../config"
import {
    updateUrlState,
    bindUrlStateChange,
    paramsToJson,
    queryStringToParams,
    unmapParams,
    getSearchUrl
} from "../urlEncoding/url"
import { Store } from "../state"
import { trackGaPageView } from "../helpers/ga"
import pipe from "../utils/pipe"
import ensureSearchQueryNumbers from "../helpers/ensureNumbers"
import { isErrorSession } from "../helpers/errorHandling"
import { deepMerge, measure } from "@nosto/search-js/utils"
import { selectorLoaded } from "../helpers/selectors"
import { renderComponent } from "./render"
import { createMapperOptions } from "../urlEncoding/createMapperOptions"
import { restoreSavedScroll, unhideNativeContent } from "../helpers/dom"

export async function initSerp(config: FullConfig, store: Store): Promise<void> {
    const end = measure("initSerp")
    unhideNativeContent(config.contentCssSelector)
    unhideNativeContent(config.categoryCssSelector)

    store.onChange(selectorLoaded, ({ initialized }) => {
        if (!initialized) {
            return
        }

        if (store.getState().pageType === "category") {
            renderComponent(config, store, {
                cssSelector: config.categoryCssSelector,
                docComponent: config.categoryComponent
            })
        } else {
            renderComponent(config, store, {
                cssSelector: config.contentCssSelector,
                docComponent: config.serpComponent
            })
        }

        if (config.preservePageScroll) {
            restoreSavedScroll()
        }
    })

    store.onChange(
        ({ query, customParams }) => ({ query, customParams }),
        ({ query, customParams }) => {
            updateUrlState(getSearchUrl({ config, store }, query, { customParams }))
            trackGaPageView()
        }
    )

    handleUrlStateChange(config, store)
    bindUrlStateChange(() => {
        handleUrlStateChange(config, store)
    })

    end()
}

export function getDataFromUrl(config: FullConfig, store: Store) {
    const parsedParams = paramsToJson(queryStringToParams(location.search))
    const queryParamKeys = Object.values(config.serpUrlMapping)
    const encodedQuery: SearchQuery = unmapParams(parsedParams, config.serpUrlMapping) || {}
    if (encodedQuery.products && !encodedQuery.products.size) {
        // making products.size available for pagination decoding
        encodedQuery.products.size = store.getInitialState().query?.products?.size
    }
    const encodedCustomParams = Object.entries(parsedParams).reduce<Record<string, unknown>>((prev, cur) => {
        if (!queryParamKeys.includes(cur[0])) {
            prev[cur[0]] = cur[1]
        }
        return prev
    }, {})

    const mapperOptions = createMapperOptions(config)
    const decoder = pipe(
        ...(config.customUrlMappings?.map(m => {
            // Wrap decoder to include mapperOptions
            return (query: unknown) => m.decode(query, mapperOptions)
        }) ?? [])
    )

    // @ts-expect-error TODO fix pipe function
    const queryPartial: SearchQuery = pipe(decoder, ensureSearchQueryNumbers)(encodedQuery)
    const query = deepMerge(store.getInitialState().query, queryPartial)
    // @ts-expect-error TODO fix pipe function
    const customParams: Record<string, unknown> = decoder(encodedCustomParams)

    return { query, customParams }
}

function handleUrlStateChange(config: FullConfig, store: Store): void {
    const { query, customParams } = getDataFromUrl(config, store)
    const isSerp = (!config.serpPath || location.pathname === config.serpPath) && !!query?.query
    const isCategory = config.isCategoryPage?.()

    if (
        (isSerp && store.getState().pageType === "search") ||
        (isCategory && store.getState().pageType === "category")
    ) {
        if (config.fallback && isErrorSession()) {
            handleFallback(config, query)
            return
        }

        newSearch({ config, store }, query, { customParams })
        return
    }

    if (store.getState().initialized) {
        location.reload()
    }
}
